import {
    useInfiniteQuery,
    useMutation,
    useQuery,
    useQueryClient
} from '@tanstack/react-query';

import {
    add,
    deleteRecord,
    fetch as fetchOne,
    fetchRecords,
    PAGE_SIZE,
    RegistrarsPage,
    update,
} from '../services/RegistrarsService';

import {Registrar} from '../types';

// FNP-943: records are React Query's job. Selection, paging and keyword stay in
// Redux. The service layer is unchanged — these hooks call the same
// RegistrarsService functions the Redux thunks called.

export const registrarsKeys = {
    all: ['registrars'] as const,
    lists: () => [...registrarsKeys.all, 'list'] as const,
    list: (filter: object) => [...registrarsKeys.lists(), filter] as const,
    details: () => [...registrarsKeys.all, 'detail'] as const,
    detail: (id: string) => [...registrarsKeys.details(), id] as const,
};

// FNP-1292 — TOKEN PAGINATION.
//
// ⚠️ `useInfiniteQuery`, never `useQuery`. The previous hook fetched exactly
// one unpaginated page and read as compliant on every other data box — it had
// a query hook, keys and invalidation — while a tenant past the first page
// simply could not see their own registrars. `listRegistrars` has always taken
// `limit` and `nextToken`; nothing on the server needed to change.
export const useRegistrarsInfiniteQuery = (filter: object = {}) => {
    return useInfiniteQuery({
        // The filter is in the key: omit it and changing the filter silently
        // serves the previous filter's cache.
        queryKey: registrarsKeys.list(filter),
        queryFn: ({pageParam}: { pageParam: string | null }): Promise<RegistrarsPage> =>
            fetchRecords(filter, PAGE_SIZE, pageParam),
        // ⚠️ MUST return `undefined` at the end of the results. Returning
        // `null`, or the same token again, makes React Query refetch forever.
        getNextPageParam: (lastPage) => {
            if (!lastPage || !lastPage.nextToken) {
                return undefined;
            }
            return lastPage.nextToken;
        },
        initialPageParam: null as string | null,
    });
};

/**
 * The flattened records, for the consumers that want a plain list.
 *
 * The three inputs, the dashboard widget, the bulk-import page and the
 * domain-names Add page all read `items` off `useRegistrars`, so the flattening
 * lives here rather than being repeated at each of them.
 */
export const useRegistrarsQuery = (filter: object = {}) => {
    const query = useRegistrarsInfiniteQuery(filter);

    // A type guard, not a bare flatMap — a partial page object would otherwise
    // throw inside a render.
    const items: Registrar[] = (query.data?.pages ?? []).flatMap(
        page => (page && 'items' in page) ? (page.items ?? []) : [],
    ).filter(Boolean);

    return {
        ...query,
        data: items,
    };
};

export const useRegistrarQuery = (registrarId?: string) => {
    return useQuery({
        queryKey: registrarsKeys.detail(registrarId ?? ''),
        queryFn: async () => {
            if (typeof registrarId === 'undefined') {
                throw new Error('registrarId is required parameter');
            }
            return await fetchOne(registrarId);
        },
        enabled: typeof registrarId !== 'undefined' && registrarId !== '',
    });
};

export const useCreateRegistrarMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (values: Registrar) => add(values),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: registrarsKeys.lists()});
        },
    });
};

export const useUpdateRegistrarMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({
                         id,
                         record
                     }: { id: string, record: Registrar }) => update(id, record),
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({queryKey: registrarsKeys.lists()});
            queryClient.invalidateQueries({queryKey: registrarsKeys.detail(variables.id)});
        },
    });
};

export const useDeleteRegistrarMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: string) => deleteRecord(id),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: registrarsKeys.lists()});
        },
    });
};
